import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CBanner, CButton, CLink } from '@appcues/component-library';
import Loader from 'components/Common/Loader';
import GuideStep from 'components/InstallationGuide/GuideStep';
import GuideHeading from 'components/InstallationGuide/GuideHeading';
import GuideParagraph from 'components/InstallationGuide/GuideParagraph';
import DomainItem from 'components/Common/DomainItem';
import { INSTALLATION_TESTING_DOCS } from 'constants/externalAppcuesResources';
import { selectAccountMeta } from 'reducers/account/meta';
import {
  selectInstalledDomains,
  selectInstalledDomainsSynced,
} from 'reducers/account/installedDomains';

export const InstallVerify = ({
  navigateToInstallDash,
  moveBackward,
  stepMax,
  stepIndex,
  areInstalledDomainsSynced,
  installedDomains = [],
  domains = '',
}) => {
  if (!areInstalledDomainsSynced) {
    return <Loader />;
  }

  // NOTE: Coercing Set to array via spreading currently not supported due to
  //       our babel configurations using core-js for IE support.
  // eslint-disable-next-line unicorn/prefer-spread
  const allDomains = Array.from(
    new Set([
      ...installedDomains.sort((a, b) => a.localeCompare(b)),
      ...domains.split(',').filter(Boolean),
    ])
  );

  return (
    <GuideStep
      stepIndex={stepIndex}
      stepMax={stepMax}
      title="Verify your installation"
      moveForward={navigateToInstallDash}
      moveBackward={moveBackward}
      customNextText="Finish Guide"
    >
      <GuideParagraph>
        Finished Installing? Verify everything is working with the steps below:
      </GuideParagraph>
      <GuideHeading>Script</GuideHeading>
      {allDomains.length > 0 ? (
        <>
          <GuideParagraph>
            We have detected the Appcues script on the domains below:
          </GuideParagraph>
          {allDomains.map(domain => (
            <DomainItem
              key={domain}
              isInstalled={installedDomains.includes(domain)}
              isInstalledClass="live"
              metaDomain={domain}
              noCheckBox
            />
          ))}
        </>
      ) : (
        <GuideParagraph>
          We haven&apos;t detected the Appcues script in your application yet.
        </GuideParagraph>
      )}
      <GuideHeading>Page tracking</GuideHeading>
      <GuideParagraph>
        Open the Appcues debugger by appending{' '}
        <code className="language-markup">?hey_appcues</code> to the URL of any
        page where Appcues is installed. Navigate around and confirm Appcues
        tracks each page change.
      </GuideParagraph>
      <GuideHeading>Identifying Users + User Properties</GuideHeading>
      <GuideParagraph>
        In the Appcues debugger, confirm the &ldquo;User identify&rdquo; section
        is checked. Expand the section to confirm that all properties are
        accurate.
      </GuideParagraph>
      <GuideHeading>Tracking events</GuideHeading>
      <GuideParagraph>
        Trigger one of the <code>Appcues.track()</code> calls you added and
        confirm it appears on the <CLink href="/settings/events">events</CLink>{' '}
        page.
      </GuideParagraph>
      <CBanner
        text="If something isn't working or you want to do more testing, check out
        our docs."
      >
        <CBanner.Actions>
          <CButton
            isExternal
            type="info"
            is="a"
            href={INSTALLATION_TESTING_DOCS}
          >
            View Docs
          </CButton>
        </CBanner.Actions>
      </CBanner>
    </GuideStep>
  );
};

InstallVerify.propTypes = {
  navigateToInstallDash: PropTypes.func,
  moveBackward: PropTypes.func,
  stepMax: PropTypes.number,
  stepIndex: PropTypes.number,
  areInstalledDomainsSynced: PropTypes.bool,
  installedDomains: PropTypes.array,
  domains: PropTypes.string,
};

const mapStateToProps = state => {
  const { domains = '' } = selectAccountMeta(state);
  return {
    areInstalledDomainsSynced: selectInstalledDomainsSynced(state),
    installedDomains: selectInstalledDomains(state),
    domains,
  };
};

export default connect(mapStateToProps)(InstallVerify);
